// src/features/chat/wsClient.ts
//
// WebSocket client for ws-gateway. See docs/architecture/ws-gateway.md §4.5
// for the wire protocol. Singleton: one active socket per tab, reconnects
// automatically with exponential backoff, and replays subscriptions after
// reconnect. Emits domain events to registered handlers.
//
// Usage:
//   const unsub = wsClient.subscribe('conv:' + convId, {
//     onMessage:        (msg) => append(msg),
//     onMessageEdited:  (msg) => update(msg),
//     onMessageDeleted: (p)   => remove(p.message_id),
//     onTyping:         (p)   => showTyping(p.profile_id),
//   });
//   // ...
//   unsub();
//
// The underlying socket is opened lazily on first subscribe and kept open
// until all subscriptions are released (last subscriber closes it). On
// reconnect, subscriptions are automatically replayed.

import { authStore } from '@core/stores';

// Feature flag — gates WS-backed live updates across the chat UI. When false,
// components should fall back to on-demand refresh (visibilitychange, manual
// refetch, etc.) without opening the socket. See framework-684.1.9.
export const WS_CHAT_ENABLED = (() => {
  const raw = (import.meta.env.VITE_ENABLE_WS_CHAT ?? 'true').toString().trim().toLowerCase();
  return raw !== 'false' && raw !== '0' && raw !== 'no' && raw !== '';
})();

type EventMap = {
  connected: { instance_id: string; heartbeat_sec: number };
  disconnected: { code: number; reason: string };
  reconnected: Record<string, never>;
  error: { error: string; code?: string };
};

type GlobalListener<K extends keyof EventMap> = (payload: EventMap[K]) => void;

// Opaque storage for per-event listener sets. We intentionally erase the
// generic parameter so the map can hold heterogeneous sets; typed getters
// below recover it.
type ListenerMap = {
  [K in keyof EventMap]?: Set<(payload: EventMap[K]) => void>;
};

type ChannelHandlers = {
  onMessage?: (data: any) => void;
  onMessageEdited?: (data: any) => void;
  onMessageDeleted?: (data: { message_id: string; conversation_id: string }) => void;
  // Server emits 'thread_reply' for ReplyInThread RPC; separate event type
  // so the main stream can ignore it and the ThreadPanel (if open) can
  // filter by parent_message_id to live-append.
  onThreadReply?: (data: any) => void;
  onTyping?: (data: any) => void;
  onReactionAdded?: (data: any) => void;
  onReactionRemoved?: (data: any) => void;
  onMessagesRead?: (data: any) => void;
  onConversationUpdated?: (data: any) => void;
  // Resource graph events (framework-mew Phase 2). Fired when chat-worker
  // processes a resource.linked / resource.unlinked Kafka event involving
  // a message in this conversation. Payload carries enough to invalidate
  // the per-message linked-resources cache and refetch via ListLinks.
  onResourceLinked?: (data: { message_id: string; source?: any; target?: any; relation_type?: string }) => void;
  onResourceUnlinked?: (data: { message_id: string; source?: any; target?: any; relation_type?: string }) => void;
  onEvent?: (type: string, data: any) => void;
};

interface Subscription {
  channel: string;
  handlers: ChannelHandlers;
}

type PendingCall = {
  resolve: (data: any) => void;
  reject: (err: Error) => void;
  timeout: number;
};

class WSClient {
  private socket: WebSocket | null = null;
  private subscriptions = new Map<string, Set<Subscription>>(); // channel -> subs
  private listeners: ListenerMap = {};
  private reconnectAttempt = 0;
  private reconnectTimer: number | null = null;
  private heartbeatTimer: number | null = null;
  private pendingOutbound: string[] = [];
  private explicitlyClosed = false;
  private connecting = false;
  // Request-response tracking for ws_callable RPCs. Each outgoing
  // `call()` registers a pending entry keyed by request_id; the next
  // incoming frame with the matching id resolves or rejects the
  // promise. Timeout guards against silently dropped responses.
  private pendingCalls = new Map<string, PendingCall>();
  private callSeq = 0;
  // True once the socket has dropped after being open; cleared on the
  // next successful open. Guards the 'reconnected' emission from firing
  // on the very first connect, and prevents listener accumulation from
  // scheduleReconnect flaps.
  private pendingReconnectNotify = false;

  // Exposed for tests / debug panels.
  get isOpen(): boolean {
    return this.socket?.readyState === WebSocket.OPEN;
  }

  subscribe(channel: string, handlers: ChannelHandlers): () => void {
    const sub: Subscription = { channel, handlers };
    let set = this.subscriptions.get(channel);
    if (!set) {
      set = new Set();
      this.subscriptions.set(channel, set);
      // First subscriber for this channel — send subscribe frame.
      this.sendFrame({ type: 'subscribe', channel });
    }
    set.add(sub);

    this.ensureConnected();

    return () => this.unsubscribe(channel, sub);
  }

  private unsubscribe(channel: string, sub: Subscription) {
    const set = this.subscriptions.get(channel);
    if (!set) return;
    set.delete(sub);
    if (set.size === 0) {
      this.subscriptions.delete(channel);
      this.sendFrame({ type: 'unsubscribe', channel });
    }
    if (this.subscriptions.size === 0) {
      // No more interest — close the socket to free resources.
      this.closeSocket();
    }
  }

  on<K extends keyof EventMap>(event: K, handler: GlobalListener<K>): () => void {
    const map = this.listeners as Record<string, Set<(p: unknown) => void>>;
    let set = map[event as string];
    if (!set) {
      set = new Set();
      map[event as string] = set;
    }
    set.add(handler as (p: unknown) => void);
    return () => set!.delete(handler as (p: unknown) => void);
  }

  private emit<K extends keyof EventMap>(event: K, payload: EventMap[K]) {
    const map = this.listeners as Record<string, Set<(p: unknown) => void>>;
    const set = map[event as string];
    if (!set) return;
    for (const handler of set) {
      try {
        handler(payload);
      } catch (err) {
        console.error('[wsClient] listener error', err);
      }
    }
  }

  private ensureConnected() {
    if (this.isOpen || this.connecting) return;
    this.connect();
  }

  private connect() {
    const token = authStore.accessToken;
    if (!token) {
      // No auth → defer until subscribe is called while logged in.
      return;
    }
    this.connecting = true;
    this.explicitlyClosed = false;

    const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
    // Single multiplexed upgrade endpoint exposed by ws-gateway — routing is
    // done by frame.Type, not URL path. See services/gateway/ws/internal/api/http/server.go.
    const url = `${proto}://${window.location.host}/ws`;

    // Auth is sent as the first frame after open — NOT via
    // Sec-WebSocket-Protocol. Browsers abort the handshake when the server
    // doesn't echo the requested subprotocol, and ws-gateway's upgrader
    // (fasthttp/websocket) doesn't echo unless Subprotocols is configured.
    // See services/gateway/ws/internal/ws/handler.go::authenticate which
    // accepts an {"type":"auth","token":"..."} frame as the first message.
    const ws = new WebSocket(url);
    this.socket = ws;

    ws.onopen = () => {
      this.connecting = false;
      this.reconnectAttempt = 0;
      // Flush a single 'reconnected' event if this open is following a
      // previous drop. Emitting here (instead of via a per-schedule
      // listener) avoids accumulating duplicate listeners on flapping
      // reconnects — the old path registered a fresh one-shot listener
      // each scheduleReconnect, then fired them all at once on recovery,
      // which triggered N parallel catch-up ListMessages calls and got
      // throttled with 429.
      if (this.pendingReconnectNotify) {
        this.pendingReconnectNotify = false;
        // Defer one tick so subscribers see the socket as fully open.
        window.setTimeout(() => this.emit('reconnected', {}), 0);
      }
      // Auth first — must be the very first frame we send. Include the
      // active profile id explicitly: the JWT we mint in dev only carries
      // user_id, and ws-gateway needs the profile id to fan out events
      // addressed to `profile:{id}`.
      ws.send(JSON.stringify({
        type: 'auth',
        token,
        profile_id: authStore.activeProfileId ?? undefined,
      }));
      // Replay subscriptions.
      for (const channel of this.subscriptions.keys()) {
        ws.send(JSON.stringify({ type: 'subscribe', channel }));
      }
      // Flush any pending frames queued while disconnected.
      for (const frame of this.pendingOutbound) ws.send(frame);
      this.pendingOutbound = [];
      // Heartbeat.
      this.startHeartbeat();
    };

    ws.onmessage = (ev) => {
      let frame: any;
      try {
        frame = JSON.parse(ev.data);
      } catch {
        return;
      }
      this.handleFrame(frame);
    };

    ws.onerror = () => {
      // Keep details out of the console spam — onclose will run next and
      // trigger the reconnect path.
    };

    ws.onclose = (ev) => {
      this.stopHeartbeat();
      this.socket = null;
      this.connecting = false;
      // Remember that a drop happened so the NEXT successful open emits
      // 'reconnected'. Flag is only set on an actual close, not on the
      // initial failed-to-open path, so subscribers don't fire catch-up
      // on first load.
      this.pendingReconnectNotify = true;
      this.emit('disconnected', { code: ev.code, reason: ev.reason });
      if (!this.explicitlyClosed && this.subscriptions.size > 0) {
        this.scheduleReconnect();
      }
    };
  }

  /**
   * Invoke a ws_callable RPC and await the response. Server handlers
   * reply with a frame of `{type}_response` (or type=error on failure)
   * carrying the same request_id. 15s timeout by default.
   *
   * Usage:
   *   const res = await wsClient.call('chat.list_messages', { conversation: {id}, pagination: {limit:50} });
   */
  call<T = any>(type: string, data: unknown, timeoutMs = 15000): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      this.callSeq += 1;
      const requestId = `${Date.now().toString(36)}-${this.callSeq.toString(36)}`;
      const timeout = window.setTimeout(() => {
        const pending = this.pendingCalls.get(requestId);
        if (!pending) return;
        this.pendingCalls.delete(requestId);
        pending.reject(new Error(`ws call ${type} timed out after ${timeoutMs}ms`));
      }, timeoutMs);
      this.pendingCalls.set(requestId, { resolve, reject, timeout });
      this.sendFrame({ type, request_id: requestId, data });
    });
  }

  private handleFrame(frame: any) {
    const type = frame.type as string;
    const requestId: string | undefined = frame.request_id;

    // Response routing takes precedence over everything else: a frame
    // carrying a request_id we recognise resolves the matching pending
    // call, regardless of its type suffix. Error frames with the same
    // request_id reject.
    if (requestId && this.pendingCalls.has(requestId)) {
      const pending = this.pendingCalls.get(requestId)!;
      this.pendingCalls.delete(requestId);
      window.clearTimeout(pending.timeout);
      if (type === 'error') {
        pending.reject(new Error(frame.error ?? frame.code ?? 'ws call failed'));
      } else {
        pending.resolve(frame.data ?? {});
      }
      return;
    }

    switch (type) {
      case 'connected':
        this.emit('connected', {
          instance_id: frame.instance_id ?? '',
          heartbeat_sec: frame.heartbeat_sec ?? 30,
        });
        return;
      case 'subscribed':
      case 'unsubscribed':
      case 'pong':
        return;
      case 'error':
        this.emit('error', { error: frame.error ?? 'unknown', code: frame.code });
        return;
      case 'drain':
        // Server told us to reconnect through the LB to another pod.
        this.socket?.close();
        return;
    }

    // Domain event — route to subscribers for this channel.
    const channel: string | undefined = frame.channel;
    if (!channel) return;
    const subs = this.subscriptions.get(channel);
    if (!subs) return;
    for (const sub of subs) {
      const h = sub.handlers;
      try {
        switch (type) {
          case 'message':
            h.onMessage?.(frame.data);
            break;
          case 'message_edited':
            h.onMessageEdited?.(frame.data);
            break;
          case 'message_deleted':
            h.onMessageDeleted?.(frame.data);
            break;
          case 'thread_reply':
            h.onThreadReply?.(frame.data);
            break;
          case 'typing':
            h.onTyping?.(frame.data);
            break;
          case 'reaction_added':
            h.onReactionAdded?.(frame.data);
            break;
          case 'reaction_removed':
            h.onReactionRemoved?.(frame.data);
            break;
          case 'messages_read':
            h.onMessagesRead?.(frame.data);
            break;
          case 'conversation_updated':
            h.onConversationUpdated?.(frame.data);
            break;
          case 'resource_linked':
            h.onResourceLinked?.(frame.data);
            break;
          case 'resource_unlinked':
            h.onResourceUnlinked?.(frame.data);
            break;
        }
        h.onEvent?.(type, frame.data);
      } catch (err) {
        console.error('[wsClient] handler error', err);
      }
    }
  }

  private sendFrame(frame: Record<string, unknown>) {
    const serialized = JSON.stringify(frame);
    if (this.isOpen) {
      this.socket!.send(serialized);
      return;
    }
    // Defer — will flush in onopen.
    this.pendingOutbound.push(serialized);
  }

  private startHeartbeat() {
    this.stopHeartbeat();
    this.heartbeatTimer = window.setInterval(() => {
      this.sendFrame({ type: 'ping' });
    }, 25000);
  }

  private stopHeartbeat() {
    if (this.heartbeatTimer !== null) {
      window.clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  private scheduleReconnect() {
    if (this.reconnectTimer !== null) return;
    const base = Math.min(30000, 1000 * Math.pow(2, this.reconnectAttempt));
    const jitter = Math.floor(Math.random() * 500);
    const delay = base + jitter;
    this.reconnectAttempt += 1;
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
      // 'reconnected' is emitted from onopen via the pendingReconnectNotify
      // flag, so scheduleReconnect doesn't register per-attempt listeners.
    }, delay);
  }

  private closeSocket() {
    this.explicitlyClosed = true;
    if (this.reconnectTimer !== null) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.stopHeartbeat();
    if (this.socket) {
      try {
        this.socket.close();
      } catch {
        // ignore
      }
      this.socket = null;
    }
    this.connecting = false;
    this.pendingOutbound = [];
  }
}

export const wsClient = new WSClient();
